// data/testimonials.ts
//
// Curated guest testimonials for the home and apartment pages carousel.
// Excerpts are NAME-FREE — original Airbnb reviews sometimes mention the
// host by name, but we quote only the portions that don't (incognito
// principle). Ellipsis "…" marks where content was omitted.
//
// KA translations are Claude-drafted and require Georgian native-speaker
// review before public launch (tracked as follow-up in the design spec).
//
// Order: newest review first.

export type Testimonial = {
  id: string
  firstName: string
  countryCode: string // ISO-3166-1 alpha-2, uppercase
  location: string
  date: string // YYYY-MM
  originalLanguage: 'en' | 'ru'
  airbnbProfileUrl: string
  photoPath?: string // e.g. '/images/guests/basel-corner-unit.jpg' — absent → initial-circle fallback
  quote: {
    en: string
    ru: string
    ka: string
  }
}

const AIRBNB_PROFILE_URL = 'https://www.airbnb.com/users/profile/1465883899966328902'

export const testimonials: readonly Testimonial[] = [
  {
    id: 'uk-family-of-four',
    firstName: 'Om Narissara',
    countryCode: 'GB',
    location: 'Stratford-upon-Avon, UK',
    date: '2025-08',
    originalLanguage: 'en',
    airbnbProfileUrl: AIRBNB_PROFILE_URL,
    photoPath: '/images/guests/uk-family-of-four.jpg',
    quote: {
      en: 'The apartment itself is super comfortable, with plenty of space for a family of 4, fast WiFi, an amazing view, and a shower with fantastic water pressure…',
      ru: 'Сам апартамент очень комфортный, места для семьи из четырёх более чем достаточно. Быстрый Wi-Fi, потрясающий вид и душ с отличным напором воды…',
      ka: 'ბინა თავად ძალიან კომფორტულია, საკმარისი სივრცე ოთხსულიანი ოჯახისთვის, სწრაფი Wi-Fi, შესანიშნავი ხედი და შხაპი წყლის ფანტასტიკური წნევით…',
    },
  },
  {
    id: 'basel-corner-unit',
    firstName: 'Alex Scripcenco',
    countryCode: 'CH',
    location: 'Basel, Switzerland',
    date: '2024-08',
    originalLanguage: 'en',
    airbnbProfileUrl: AIRBNB_PROFILE_URL,
    photoPath: '/images/guests/basel-corner-unit.jpg',
    quote: {
      en: "The place is absolutely phenomenal, it's a corner unit on the 13th floor with a wrap around balcony, so you get amazing views of the mountains, the city line of Batumi and the sea. All the furniture is brand new and well looked after, there is tons of space inside and outside… The bed is comfortable and the AC works really well…",
      ru: 'Квартира абсолютно феноменальная — угловая на 13-м этаже с балконом по двум сторонам, поэтому у вас потрясающие виды на горы, огни Батуми и море. Вся мебель новая и ухоженная, много места и внутри, и снаружи… Кровать удобная, и кондиционер работает отлично…',
      ka: 'ბინა ნამდვილად ფენომენალურია — კუთხის ბინა მე-13 სართულზე გარშემო მოქცეული აივნით, საიდანაც იშლება შესანიშნავი ხედი მთებზე, ბათუმის ზღვის სანაპიროზე და ზღვაზე. მთელი ავეჯი ახალია და კარგადაა მოვლილი, ბევრი ადგილია შიგნითაც და გარეთაც… საწოლი კომფორტულია, კონდიციონერი მშვენივრად მუშაობს…',
    },
  },
  {
    id: 'astana-balcony-panorama',
    firstName: 'Max',
    countryCode: 'KZ',
    location: 'Astana, Kazakhstan',
    date: '2024-08',
    originalLanguage: 'ru',
    airbnbProfileUrl: AIRBNB_PROFILE_URL,
    photoPath: '/images/guests/astana-balcony-panorama.jpg',
    quote: {
      en: 'A spectacular view from the windows and the balcony! An enormous balcony that gives you nearly a 360-degree panorama around you… Excellent sleeping arrangements and spacious apartments. Highly recommend.',
      ru: 'Шикарный вид из окон и балкона! Просто огромный балкон, с которого открывается панорама почти на 360 градусов вокруг… Отличные спальные места и просторные апартаменты. Рекомендую!',
      ka: 'ფანტასტიკური ხედი ფანჯრებიდან და აივნიდან! უზარმაზარი აივანი, საიდანაც თითქმის 360 გრადუსიანი პანორამა იშლება… შესანიშნავი საწოლები და ფართო ბინები. გირჩევთ!',
    },
  },
  {
    id: 'beersheba-family-with-kids',
    firstName: 'Alex',
    countryCode: 'IL',
    location: 'Beersheba, Israel',
    date: '2024-08',
    originalLanguage: 'en',
    airbnbProfileUrl: AIRBNB_PROFILE_URL,
    photoPath: '/images/guests/beersheba-family-with-kids.jpg',
    quote: {
      en: 'Perfect for couples, and families (especially with children. We came with 3). The view from both balconies is just beautiful!… We would come back without any hesitation.',
      ru: 'Идеально подходит для пар и семей (особенно с детьми — мы приехали с тремя). Вид с обоих балконов просто прекрасный!… Вернёмся без раздумий.',
      ka: 'იდეალურია წყვილებისთვის და ოჯახებისთვის (განსაკუთრებით ბავშვებით — ჩვენ სამით ჩამოვედით). ხედი ორივე აივნიდან უბრალოდ მშვენიერია!… უყოყმანოდ დავბრუნდებით.',
    },
  },
  {
    id: 'tbilisi-romantic-view',
    firstName: 'Татьяна',
    countryCode: 'GE',
    location: 'Tbilisi, Georgia',
    date: '2023-09',
    originalLanguage: 'ru',
    airbnbProfileUrl: AIRBNB_PROFILE_URL,
    photoPath: '/images/guests/tbilisi-romantic-view.jpg',
    quote: {
      en: 'A wonderful place where you truly feel at home… The apartment is spotless, all the furniture is quality-made and still smells of new wood. A view for romantics — sunset over the sea, Batumi ablaze with lights, and the mountains all at once…',
      ru: 'Прекрасное жильё, в котором действительно чувствуешь себя как дома… Номер очень чистый, вся мебель качественная и до сих пор пахнет новым деревом. Вид для романтиков — и закат на море, и горящий огнями Батуми, и горы одновременно…',
      ka: 'შესანიშნავი საცხოვრებელი, სადაც მართლაც შენი სახლივით გრძნობ თავს… ბინა ბრწყინვალედ სუფთაა, ავეჯი ხარისხიანია და ახალი ხის სუნი ჯერაც აქვს. რომანტიკოსების ხედი — ზღვაზე ჩასვლის მზე, ბათუმი შუქებით აელვარებული და მთები ერთდროულად…',
    },
  },
  {
    id: 'tallinn-picture-vs-reality',
    firstName: 'Hannes',
    countryCode: 'EE',
    location: 'Tallinn, Estonia',
    date: '2023-08',
    originalLanguage: 'en',
    airbnbProfileUrl: AIRBNB_PROFILE_URL,
    photoPath: '/images/guests/tallinn-picture-vs-reality.jpg',
    quote: {
      en: 'In Georgia, building quality is often below standard, so while the pictures look nice, the reality can turn out to be something completely different. This was not the case here. Everything was classy and done properly, starting with pools and recreational areas and finishing with the apartment itself…',
      ru: 'В Грузии качество строительства часто ниже стандарта, и бывает, что на фото красиво, а в реальности всё совсем иначе. Здесь — не тот случай. Всё сделано стильно и добротно — от бассейнов и зон отдыха до самого апартамента…',
      ka: 'საქართველოში მშენებლობის ხარისხი ხშირად სტანდარტს ჩამოუვარდება, და ხდება, რომ ფოტოებზე ლამაზია, სინამდვილეში კი სულ სხვა რამ გამოდის. აქ ეს ასე არ იყო. ყველაფერი სტილურად და ხარისხიანად იყო გაკეთებული — აუზებიდან და დასასვენებელი ზონებიდან თავად ბინამდე…',
    },
  },
  {
    id: 'nj-spacious-balconies',
    firstName: 'Mariam Sophie',
    countryCode: 'US',
    location: 'Palisades Park, NJ',
    date: '2023-08',
    originalLanguage: 'en',
    airbnbProfileUrl: AIRBNB_PROFILE_URL,
    photoPath: '/images/guests/nj-spacious-balconies.jpg',
    quote: {
      en: 'A beautiful, spacious, peaceful apartment that was extremely clean and equipped with everything you could possibly need during your stay… The apartment was even more spacious than I expected, with amazing balconies that had beautiful views from both sides…',
      ru: 'Красивый, просторный, тихий апартамент — идеально чистый и укомплектованный всем, что может понадобиться во время поездки… Оказался даже просторнее, чем я ожидала, с великолепными балконами и видами с обеих сторон…',
      ka: 'ლამაზი, ფართო, მშვიდი ბინა — უკიდურესად სუფთა და აღჭურვილი ყველაფრით, რაც შეიძლება გჭირდებოდეთ ყოფნის დროს… ბინა კიდევ უფრო ფართო აღმოჩნდა, ვიდრე ველოდი, შესანიშნავი აივნებითა და მშვენიერი ხედებით ორივე მხრიდან…',
    },
  },
]
